import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  CheckCircle,
  WarningCircle,
  XCircle,
  EnvelopeSimple,
  ChatCircleText,
  Eye,
  Lightning,
  CreditCard,
  Sparkle,
  Wallet,
  Bell,
} from "@phosphor-icons/react";

const TYPE_ICONS = {
  issued: { Icon: Sparkle, tint: "text-[#8C6B21] bg-[#CBA35C]/15" },
  activated: { Icon: Lightning, tint: "text-[#184A30] bg-[#184A30]/10" },
  email_sent: { Icon: EnvelopeSimple, tint: "text-[#184A30] bg-[#184A30]/10" },
  email_failed: { Icon: EnvelopeSimple, tint: "text-[#B04E2F] bg-[#B04E2F]/10" },
  sms_sent: { Icon: ChatCircleText, tint: "text-[#184A30] bg-[#184A30]/10" },
  sms_delivered: { Icon: ChatCircleText, tint: "text-[#184A30] bg-[#184A30]/10" },
  sms_failed: { Icon: ChatCircleText, tint: "text-[#B04E2F] bg-[#B04E2F]/10" },
  opened: { Icon: Eye, tint: "text-[#D96C4A] bg-[#D96C4A]/10" },
  scan: { Icon: Eye, tint: "text-[#184A30] bg-[#184A30]/10" },
  wallet_added: { Icon: Wallet, tint: "text-[#184A30] bg-[#184A30]/10" },
  spend: { Icon: CreditCard, tint: "text-[#184A30] bg-[#184A30]/10" },
  spend_declined: { Icon: CreditCard, tint: "text-[#8C6B21] bg-[#CBA35C]/15" },
};

const STATUS_DOT = {
  ok: <CheckCircle size={12} weight="fill" className="text-[#184A30]" />,
  warn: <WarningCircle size={12} weight="fill" className="text-[#CBA35C]" />,
  err: <XCircle size={12} weight="fill" className="text-[#B04E2F]" />,
};

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, (now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function metaLine(ev) {
  const m = ev.meta || {};
  switch (ev.type) {
    case "issued":
      return m.receiver ? `for ${m.receiver}` : "";
    case "activated":
      return m.last4 ? `card ending ${m.last4}` : "";
    case "email_sent":
      return [m.to, m.auto ? "(auto-send on activation)" : null].filter(Boolean).join(" · ");
    case "email_failed":
      return [m.to, m.error].filter(Boolean).join(" · ");
    case "sms_sent":
    case "sms_delivered":
      return m.to || "";
    case "sms_failed":
      return [m.to, m.error_code ? `error ${m.error_code}` : null, m.error].filter(Boolean).join(" · ");
    case "scan":
      return m.location || "from a new device";
    case "wallet_added":
      return m.platform === "apple" ? "Apple Wallet" : m.platform === "google" ? "Google Wallet" : "";
    case "spend":
    case "spend_declined": {
      const amt = (m.amount_usd || 0).toFixed(2);
      return m.merchant ? `$${amt} at ${m.merchant}` : `$${amt}`;
    }
    default:
      return "";
  }
}

/**
 * Granular per-card activity feed. Polls the backend `/cards/:id/timeline`
 * endpoint every 30 s while the page is visible so senders see live
 * updates without manual refresh — but pauses when the tab is in the
 * background to avoid background fetches stacking up.
 *
 * Renders newest-first. Empty state explains what kind of events
 * will appear once the receiver interacts with the card.
 *
 * Props:
 *   cardId — the card to render the timeline for
 *   isOwner — true when current viewer issued this card. Drives whether
 *             the "Notify me on first spend" toggle is visible/editable.
 *   notifyOnFirstSpend — initial state of the opt-in flag (from `card`)
 *   firstSpendNotified — whether the first-spend alert has already fired
 *                        (so we can hide the toggle once it's moot)
 */
export default function CardActivityTimeline({
  cardId,
  refreshKey = 0,
  isOwner = false,
  notifyOnFirstSpend = false,
  firstSpendNotified = false,
}) {
  const [events, setEvents] = useState(null);
  const [notifyEnabled, setNotifyEnabled] = useState(!!notifyOnFirstSpend);
  const [notifySaving, setNotifySaving] = useState(false);

  // Sync prop changes from parent (card refetch)
  useEffect(() => { setNotifyEnabled(!!notifyOnFirstSpend); }, [notifyOnFirstSpend]);

  const toggleNotify = useCallback(async (next) => {
    setNotifySaving(true);
    setNotifyEnabled(next); // optimistic
    try {
      await api.patch(`/cards/${cardId}/notify-on-first-spend`, { enabled: next });
      toast.success(
        next
          ? "We'll email you the moment your recipient taps to pay for the first time."
          : "Notification turned off."
      );
    } catch {
      setNotifyEnabled(!next); // rollback
      toast.error("Couldn't save — please try again.");
    } finally {
      setNotifySaving(false);
    }
  }, [cardId]);

  const load = useCallback(async () => {
    if (!cardId) return;
    try {
      const r = await api.get(`/cards/${cardId}/timeline?limit=40`);
      setEvents(r?.data?.events || []);
    } catch {
      setEvents([]);
    }
  }, [cardId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!cardId) return undefined;
    const id = setInterval(() => {
      if (typeof document === "undefined" || !document.hidden) load();
    }, 30000);
    return () => clearInterval(id);
  }, [cardId, load]);

  if (events === null) return null; // initial mount, suppress flash

  return (
    <div className="mt-10" data-testid="card-activity-timeline">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Recipient activity</p>
          <h3 className="serif text-2xl text-[#112318]">Timeline</h3>
        </div>
        {events.length > 0 && (
          <p className="text-[11px] text-[#4A5C50]" data-testid="timeline-count">
            {events.length} event{events.length === 1 ? "" : "s"}
          </p>
        )}
      </div>

      {/* First-spend opt-in toggle. Owner-only. Hidden once already fired
          (no point asking after the fact). Switches to a confirmation
          line so the sender knows the email already went out. */}
      {isOwner && firstSpendNotified && (
        <div
          className="mb-3 rounded-2xl border border-[#184A30]/20 bg-[#184A30]/5 p-3 text-sm text-[#184A30] flex items-start gap-2"
          data-testid="first-spend-notified-banner"
        >
          <Bell size={16} weight="fill" className="text-[#184A30] mt-0.5 shrink-0" />
          <span className="leading-relaxed">
            We notified you when this card was first tapped to pay. (You can find the email in your inbox.)
          </span>
        </div>
      )}
      {isOwner && !firstSpendNotified && (
        <label
          className="mb-3 rounded-2xl border border-[#E5E0D8] bg-white p-3 flex items-start gap-3 cursor-pointer hover:border-[#184A30]/40 transition-colors"
          data-testid="first-spend-toggle-row"
        >
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-[#184A30]"
            checked={notifyEnabled}
            disabled={notifySaving}
            onChange={(e) => toggleNotify(e.target.checked)}
            data-testid="first-spend-toggle"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#112318] flex items-center gap-1.5">
              <Bell size={14} weight={notifyEnabled ? "fill" : "regular"} className={notifyEnabled ? "text-[#D96C4A]" : "text-[#4A5C50]"} />
              Notify me when my recipient first taps to pay
            </p>
            <p className="text-[12px] text-[#4A5C50] mt-0.5 leading-relaxed">
              We'll send you one email the moment a charge lands on this card. International senders also get an SMS.
            </p>
          </div>
        </label>
      )}

      {events.length === 0 && (
        <div
          className="rounded-2xl border border-[#E5E0D8] bg-white p-5 text-sm text-[#4A5C50]"
          data-testid="timeline-empty"
        >
          No activity yet. Once your recipient opens the email or scans the QR, you'll see "card viewed", wallet adds, and tap-to-pay events appear here in real time.
        </div>
      )}

      {events.length > 0 && (
        <ol className="rounded-2xl border border-[#E5E0D8] bg-white">
          {events.map((ev, i) => {
            const cfg = TYPE_ICONS[ev.type] || { Icon: CheckCircle, tint: "text-[#4A5C50] bg-[#FBF9F6]" };
            const Icon = cfg.Icon;
            const meta = metaLine(ev);
            const isLast = i === events.length - 1;
            return (
              <li
                key={`${ev.ts}-${ev.type}-${i}`}
                className={`flex items-start gap-3 p-3 ${isLast ? "" : "border-b border-[#E5E0D8]"}`}
                data-testid={`timeline-event-${ev.type}`}
              >
                <span className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${cfg.tint}`}>
                  <Icon size={16} weight="duotone" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#112318]">{ev.label}</span>
                    {STATUS_DOT[ev.status] || null}
                  </div>
                  {meta && (
                    <p className="text-xs text-[#4A5C50] mt-0.5 truncate">{meta}</p>
                  )}
                </div>
                <span
                  className="text-[11px] text-[#4A5C50] mono shrink-0 pt-1"
                  title={new Date(ev.ts).toLocaleString()}
                >
                  {relativeTime(ev.ts)}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
